"use client";

import { useChannelManagerPage } from "./hooks/use-channel-manager-page";
import { useSeo } from "@/features/shared/lib/use-seo";
import {
  HeroSection,
  VantagensSection,
  AplicativoSection,
  RecursosSection,
  //ChannelManagerFlow,
} from "./components";
import { GenericInfoSection } from "@/features/shared/components/generic-info-section";
import { INFO_SECTIONS } from "@/features/shared/content/info-sections";

// Provas Sociais importadas da Home e UI
import { NumerosSection } from "@/features/home/components/numeros-section";
import { NaMidiaSection } from "@/features/home/components/na-midia-section";
import { SoftwareProductsCarousel, TrustedLogosMarquee, SmartIntegrationsTabs, WallOfLoveSection, DorParallaxSection } from "@/features/shared/components";
import { FAQAccordion } from "@/features/ui/components/faq-accordion";
import { LeadCaptureCTA } from "@/features/ui/components/lead-capture-cta";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { UltimasDoBlogSection } from "@/features/shared/components/ultimas-do-blog-section-lazy";
import { getProdutoIcone } from "@/features/shared/data/produtos-data";

import { Spinner } from "@/components/ui/spinner";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

import { artigosMidia, depoimentos, numeros, videosData } from "@/features/shared/data/social-proof-data";

const LEAD_TITLE = "Channel Manager";

function ChannelManagerPage() {
  useSeo({
    title: "Channel Manager Hoteleiro | +800 canais | Foco Tecnologia",
    description:
      "Conecte seu hotel a mais de 850 OTAs e metasearch engines com sincronização automática de tarifas, disponibilidade e reservas em tempo real.",
    path: "/gestor-de-canais-channel-manager",
  });

  const { openLeadCapture } = useLeadCapture();
  const { data, isLoading, isError, refetch } = useChannelManagerPage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-12 h-12" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Empty className="text-center">
          <EmptyHeader>
            <EmptyTitle>Erro ao carregar dados</EmptyTitle>
            <EmptyDescription>
              Não foi possível carregar o conteúdo da página. Tente novamente.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-0">

      {/* Section 1: Hero */}
      <HeroSection
        data={data.hero}
        onCtaClick={() => openLeadCapture({ source: "form-gestor-de-canais-channel-manager", title: LEAD_TITLE, icon: getProdutoIcone("/gestor-de-canais-channel-manager") })}
      />

      {/* Section 2: O que é um gestor de canais */}
      <GenericInfoSection {...INFO_SECTIONS.channelManager} imageSide="right" />

      {/* Diagnóstico: baixa ocupação (mesmo cenário da home) */}
      <DorParallaxSection
        dorId="baixa-ocupacao"
        backgroundImage="/assets/imgs/motor-de-reservas/dor.webp"
        backgroundAlt="Lobby de hotel vazio à noite, recepcionista sozinha aguardando hóspedes"
      />

      {/* Section 3: Fluxo de sincronização */}
      {/*<ChannelManagerFlow />*/}

      {/* Section 4: Vantagens do gestor de canais */}
      <VantagensSection />

      {/* Section 5: Aplicativo mobile */}
      <AplicativoSection />

      {/* Section 6: Recursos */}
      <RecursosSection />

      {/* Integrações Inteligentes */}
      <SmartIntegrationsTabs />

      {/* Prova Social: Mural de depoimentos (texto + vídeo, unificados) */}
      <WallOfLoveSection
        depoimentos={depoimentos}
        videos={videosData}
        badge="Depoimentos"
        title="Um mural de resultados reais"
        subtitle="Cada card aqui é um hoteleiro de verdade, contando como a Foco eliminou o overbooking e aumentou a ocupação."
      />

      {/* Prova Social: Na Mídia */}
      <NaMidiaSection artigos={artigosMidia} />

      {/* Bloco de Autoridade e Confiança */}
      <NumerosSection numeros={numeros} />
      <TrustedLogosMarquee />

      {/* Prova Social: Carrossel de Produtos */}
      <SoftwareProductsCarousel />

      <UltimasDoBlogSection productSlug="gestor-de-canais-channel-manager" />

      {/* Prova Social: FAQ */}
      <FAQAccordion
        items={[
          { id: "1", question: "O que é um Channel Manager?", answer: "Um Channel Manager (ou Gestor de Canais) é um software que sincroniza automaticamente as reservas, disponibilidade e tarifas do seu hotel em todos os canais de vendas conectados, eliminando o risco de overbooking." },
          { id: "2", question: "Com quantos canais o Channel Manager da Foco integra?", answer: "Nosso Channel Manager integra com Mais de 800 canais de vendas, incluindo as principais OTAs globais e regionais, GDS, canais corporativos e o motor de reservas do seu site." },
          { id: "3", question: "Posso automatizar o pagamento das reservas do gestor de canais?", answer: "Através do sistema Foco Pay, todas as reservas do Gestor de canais e Motor de reservas podem ser cobradas automaticamente pelo robô, de acordo com configurações pré determinadas para cada canal, política ou tipo de cartão de crédito." },
          { id: "4", question: "O Channel Manager integra com meu PMS?", answer: "Sim, oferecemos integração two-way com os principais PMS do mercado brasileiro, garantindo sincronização completa de reservas, cancelamentos e alterações." },
          { id: "5", question: "Posso gerenciar pelo celular?", answer: "Sim! Nosso aplicativo mobile permite que você gerencie tarifas, disponibilidade, reservas e visualize relatórios de qualquer lugar, a qualquer momento." },
        ]}
        title="Dúvidas Frequentes"
        subtitle="Tire suas dúvidas sobre o Channel Manager"
        badge="FAQ"
        showContactButton
      />

      {/* Lead Capture CTA */}
      <LeadCaptureCTA
        title="Pronto para eliminar o overbooking e aumentar sua ocupação?"
        subtitle="Solicite uma demonstração e descubra como o Channel Manager da Foco pode transformar a gestão do seu hotel."
        badge="Comece agora"
        source="gestor-de-canais-channel-manager"
        leadTitle={LEAD_TITLE}
      />
    </div>
  );
}

export { ChannelManagerPage };
